'use client';

import { useEffect, useMemo, useRef } from 'react';

import { mergeContextualData } from '../contextualData';
import type { ScopedObservability } from '../declarations/Observability';
import { globalObservability } from '../global';
import { getDeployEnv } from '../internal/deployEnv';
import { useContextualData } from './ContextualDataContext';

let hasWarnedUninitialized = false;

/**
 * Telemetry must never crash a host. This previously threw outside production, which
 * also fired during SSR in production builds — `getDeployEnv()` reports 'development'
 * when there is no `window`.
 */
const dispatch = (send: () => void): void => {
  if (!globalObservability.isInitialized()) {
    if (getDeployEnv() !== 'production' && !hasWarnedUninitialized) {
      hasWarnedUninitialized = true;
      // eslint-disable-next-line no-console
      console.warn('[observability] call initObservability before tracking; events are dropped');
    }
    return;
  }
  try {
    send();
  } catch {
    // A throwing sink must not take the app down.
  }
};

/** Gives you access to Observability methods, with ContextualData from the context automatically added. */
export function useObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
>(): ScopedObservability<AppCategory, AppActions> {
  const contextualData = useContextualData();

  // Store contextualData in a ref so we don't update the track functions when contextualData changes.
  // Previously, any useEffect that depended on a track function would get called again whenever
  // contextualData changed, which is not the behavior people were expecting.
  const contextualDataRef = useRef(contextualData);
  useEffect(() => {
    contextualDataRef.current = contextualData;
  }, [contextualData]);

  return useMemo<ScopedObservability<AppCategory, AppActions>>(
    () => ({
      /**
       * @deprecated This API sends legacy Segment analytics. Use `trackJourneyEvent` for
       * durable product behavior, `trackInternalEvent` for engineering diagnostics,
       * `trackError` for failures, or `markTiming` for durations.
       */
      // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
      trackEvent: (action, label, data, options) => {
        dispatch(() => {
          // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
          globalObservability.trackEvent(
            action,
            label,
            mergeContextualData(contextualDataRef.current, { data }),
            options,
          );
        });
      },

      /**
       * @deprecated Use `trackJourneyEvent` for durable product behavior.
       * @see https://knowledgebase.apps.opendoor.com/doc/product/customer-demand/product-eng-org/operations-runbooks/how-to-create-journey-2-0-events.md
       */
      // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
      trackUserEvent: (event) => {
        dispatch(() => {
          // oxlint-disable-next-line typescript/no-deprecated -- The compatibility API stays available until consumer calls are removed
          globalObservability.trackUserEvent(event, contextualDataRef.current);
        });
      },

      trackJourneyEvent: (categoryName, eventName, eventData, options) => {
        dispatch(() => {
          globalObservability.trackJourneyEvent(
            categoryName,
            eventName,
            eventData,
            contextualDataRef.current,
            options,
          );
        });
      },

      trackError: (label, error, data, options) => {
        dispatch(() => {
          globalObservability.trackError(
            label,
            error,
            mergeContextualData(contextualDataRef.current, { data }),
            options,
          );
        });
      },

      trackInternalEvent: (name, data) => {
        dispatch(() => {
          globalObservability.trackInternalEvent(
            name,
            mergeContextualData(contextualDataRef.current, { data }),
          );
        });
      },

      markTiming: (name, time) => {
        dispatch(() => {
          globalObservability.markTiming(name, time);
        });
      },
    }),
    [],
  );
}
